import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import SketchPreview from './SketchPreview';
import { ConditionChip, ConditionSearch } from './ConditionPicker';
import { CONDITION_STATUS, conditionStatus } from './generatePedigree';
import { CHROME, MICRO_LABEL } from './tokens';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Every distinct condition, in the model's own words: only an EXACT
// disease-name match is applied, so the words are what the clinician checks.
function conditionsIn(generations) {
  const terms = new Set();
  for (const g of generations) {
    for (const p of g.people) for (const c of p.conditions || []) terms.add(c);
  }
  return [...terms];
}

export default function ResultSummary({
  result,
  accent,
  onEdit,
  onApply,
  applying,
  error,
  needsReplace,
  choices = {},
  onChoose
}) {
  const t = useAutoTranslation();

  const { config, people, generations, warnings } = result;
  const terms = conditionsIn(generations);
  const [picking, setPicking] = React.useState(null);
  const codedNameByTerm = new Map(
    (result.conditions?.coded || []).map((c) => [c.term, c.name])
  );
  const nearestByTerm = new Map(
    (result.conditions?.unresolved || []).map((u) => [u.term, u.nearest])
  );
  // Counted from the DRAWN symbols, so the header and the picture agree.
  const affected = generations.reduce(
    (n, g) =>
      n + g.people.filter((p) => (p.conditions || []).length > 0).length,
    0
  );

  const statuses = terms.map((t) =>
    conditionStatus(t, result.conditions, choices)
  );
  const summaryLine = statuses.includes(CONDITION_STATUS.unchecked)
    ? t(
        'Not checked against the terminology service. Anything left uncoded is recorded as Other Disease/Comment. Click a condition to code it yourself.'
      )
    : statuses.includes(CONDITION_STATUS.other)
    ? t(
        'Grey conditions are recorded as Other Disease/Comment, with their wording in the comment. Click any condition to choose its code.'
      )
    : t(
        'All of these will be coded when you apply. Click any condition to change its code.'
      );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between'
        }}
      >
        <Typography
          sx={{ fontSize: '0.85rem', fontWeight: 600, color: CHROME.text }}
        >
          {t('Sketched pedigree')}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: CHROME.muted }}>
          {people} {people === 1 ? t('person') : t('people')} ·{' '}
          {generations.length}{' '}
          {generations.length === 1 ? t('generation') : t('generations')}
          {affected ? ` · ${t('{{affected}} affected', { affected })}` : ''}
        </Typography>
      </Box>

      <Box sx={{ mt: 1 }}>
        <SketchPreview
          config={config}
          conditions={result.conditions}
          choices={choices}
        />
        <Box
          sx={{
            mt: 0.5,
            fontSize: '0.66rem',
            color: CHROME.muted,
            lineHeight: 1.45
          }}
        >
          {t(
            'Drag to pan, scroll to zoom. Dark is a coded condition, grey is Other Disease/Comment; disease colours are chosen when you apply. Numbers are the sketch’s own, not UPNs.'
          )}
        </Box>
      </Box>

      {warnings.length > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            mt: 1,
            py: 0.25,
            fontSize: '0.72rem',
            lineHeight: 1.45,
            '& .MuiAlert-icon': { fontSize: 15, mr: 1 }
          }}
        >
          {warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </Alert>
      )}

      {terms.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ ...MICRO_LABEL, color: CHROME.muted, mb: 0.5 }}>
            {t('Conditions')}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {terms.map((term) => (
              <ConditionChip
                key={term}
                term={term}
                status={conditionStatus(term, result.conditions, choices)}
                choice={choices?.[term]}
                codedName={codedNameByTerm.get(term)}
                nearest={nearestByTerm.get(term)}
                accent={accent}
                onOpen={(el, t) => setPicking({ el, term: t })}
              />
            ))}
          </Box>
          <Box
            sx={{
              mt: 0.5,
              fontSize: '0.66rem',
              color: CHROME.muted,
              lineHeight: 1.45
            }}
          >
            {summaryLine}
          </Box>
          <ConditionSearch
            anchorEl={picking?.el || null}
            term={picking?.term}
            status={
              picking
                ? conditionStatus(picking.term, result.conditions, choices)
                : null
            }
            choice={picking ? choices?.[picking.term] : null}
            accent={accent}
            onChoose={onChoose}
            onClose={() => setPicking(null)}
          />
        </Box>
      )}

      {error && (
        <Typography
          sx={{
            mt: 1,
            fontSize: '0.72rem',
            color: 'error.main',
            lineHeight: 1.45
          }}
        >
          {error}
        </Typography>
      )}

      <Box
        sx={{
          mt: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Button
          size="small"
          onClick={onEdit}
          disabled={applying}
          sx={{
            textTransform: 'none',
            color: CHROME.muted,
            fontSize: '0.78rem'
          }}
        >
          {t('Edit input')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={onApply}
          disabled={applying}
          sx={{
            textTransform: 'none',
            fontSize: '0.78rem',
            fontWeight: 600,
            backgroundColor: accent,
            '&:hover': { backgroundColor: accent, filter: 'brightness(0.93)' }
          }}
        >
          {applying
            ? t('Applying…')
            : needsReplace
            ? t('Replace and apply')
            : t('Apply to canvas')}
        </Button>
      </Box>
    </Box>
  );
}
